/**
 * ClaimRecordPage — landing for the "Claim this ancestor" CTA on
 * genealogic.ai/record/:id. Query: ?id=<external_record_id>.
 *
 * Flow:
 *   1. Fetch the record (public, no auth).
 *   2. Fetch the caller's trees; if none, prompt to create one.
 *   3. Let the user pick a destination tree.
 *   4. POST /api/records/:id/claim { tree_id } → returns new person row.
 *   5. Redirect to the new person's full page.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../services/api.js'
import UpgradeInline from '../components/shared/UpgradeInline.jsx'

export default function ClaimRecordPage() {
  const [params] = useSearchParams()
  const recordId = params.get('id')
  const navigate = useNavigate()

  const [record, setRecord] = useState(null)
  const [trees, setTrees] = useState([])
  const [selectedTree, setSelectedTree] = useState('')
  const [newTreeName, setNewTreeName] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [tierErr, setTierErr] = useState(null)

  useEffect(() => {
    if (!recordId) {
      setError('No record id in URL.')
      setLoading(false)
      return
    }
    Promise.all([
      api.get(`/records/${recordId}`).catch(() => null),
      api.get('/trees').catch(() => []),
    ]).then(([rec, trs]) => {
      setRecord(rec)
      setTrees(Array.isArray(trs) ? trs : [])
      if (trs?.length) setSelectedTree(trs[0].id)
      setLoading(false)
    })
  }, [recordId])

  async function handleClaim() {
    setSubmitting(true); setError(null); setTierErr(null)
    try {
      let treeId = selectedTree
      if (!treeId) {
        const tree = await api.post('/trees', { name: newTreeName || `${record.last_name || 'My'} family tree` })
        treeId = tree.id
      }
      const result = await api.post(`/records/${recordId}/claim`, { tree_id: treeId })
      navigate(`/tree/${treeId}/person/${result.person.id}`)
    } catch (e) {
      if (e.code === 'TIER_LIMIT') setTierErr(e)
      else setError(e.message || 'Could not claim this record.')
      setSubmitting(false)
    }
  }

  if (loading) return <div style={{ padding: '3rem' }}>Loading record…</div>
  if (!record) return (
    <div style={{ padding: '3rem', maxWidth: 640 }}>
      <h1>Record not found</h1>
      <p>This record may have been removed, or the link is incorrect.</p>
      <a href="https://genealogic.ai/records" className="btn btn--ghost">Browse records</a>
    </div>
  )

  const name = [record.first_name, record.patronymic, record.last_name].filter(Boolean).join(' ') || 'Unknown'
  const byear = record.birth_year || (record.birth_date && (record.birth_date.match(/\d{4}/) || [])[0])
  const dyear = record.death_year || (record.death_date && (record.death_date.match(/\d{4}/) || [])[0])
  const yrs = byear && dyear ? `${byear}–${dyear}` : byear ? `b. ${byear}` : dyear ? `d. ${dyear}` : null

  return (
    <div style={{ padding: '2.5rem 2rem', maxWidth: 640, margin: '0 auto' }}>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.75rem', marginBottom: '0.25rem' }}>
        Claim <em>{name}</em>{yrs ? ` (${yrs})` : ''}
      </h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
        A new person will be created in your tree, populated from this record's fields,
        with the record kept as a cited source in the notes.
      </p>

      <div style={{
        background: 'var(--paper)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.75rem',
        fontSize: '0.9rem'
      }}>
        <div><strong>Record type:</strong> {record.record_type || '—'}</div>
        {record.birth_place && <div><strong>Birthplace:</strong> {record.birth_place}</div>}
        {record.death_place && <div><strong>Place of death:</strong> {record.death_place}</div>}
        {(record.source_archive || record.source_collection) && (
          <div><strong>Source:</strong> {[record.source_archive, record.source_collection].filter(Boolean).join(' — ')}</div>
        )}
      </div>

      {trees.length > 0 ? (
        <label style={{ display: 'block', marginBottom: '1.5rem' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block', marginBottom: '0.4rem' }}>
            Add to tree
          </span>
          <select
            value={selectedTree}
            onChange={e => setSelectedTree(e.target.value)}
            style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.95rem' }}
          >
            {trees.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      ) : (
        <label style={{ display: 'block', marginBottom: '1.5rem' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'block', marginBottom: '0.4rem' }}>
            Name your first tree
          </span>
          <input
            type="text"
            value={newTreeName}
            onChange={e => setNewTreeName(e.target.value)}
            placeholder={`${record.last_name || 'My'} family tree`}
            style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.95rem' }}
          />
        </label>
      )}

      {tierErr && <UpgradeInline err={tierErr} />}
      {error && <div style={{ color: 'var(--danger, #b54040)', marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '0.6rem' }}>
        <button className="btn btn--primary" onClick={handleClaim} disabled={submitting}>
          {submitting ? 'Claiming…' : 'Add to tree →'}
        </button>
        <button className="btn btn--ghost" onClick={() => navigate('/')} disabled={submitting}>
          Cancel
        </button>
      </div>
    </div>
  )
}
